import { Amplify } from 'aws-amplify';
import { signInWithRedirect, fetchAuthSession, signOut } from 'aws-amplify/auth';
import { AmplifyConfig } from './models/AmplifyConfig.model';

export class AmplifyService {
  private amplifyConfig!: AmplifyConfig;

  private async getSourceAmplifyConfig(): Promise<AmplifyConfig | null> {
    if (process.env.NODE_ENV === 'development') {
      return {
        cognito: {
          userPoolId: 'us-east-1_qJYgUBbWp',
          userPoolClientId: '6k1e6rakav4g5frmm0co87b4mk',
          userPoolAuthUrl: 'ca-marketplace-dev.auth.us-east-1.amazoncognito.com',
          region: 'us-east-1',
        },
        api: {
          invokeUrl: 'https://wvvd2p6fmd.execute-api.us-east-1.amazonaws.com/dev',
          name: 'DevtoolsMarketplaceApi',
          apiId: 'wvvd2p6fmd',
        },
      };
    }

    try {
      const configPath = `/static/json/config.json?t=${Date.now()}`;
      const response = await fetch(configPath);
      return response.json();
    } catch {
      return null;
    }
  }

  public getRedirectUrl(): string {
    return window.location.origin;
  }

  public getAuthenticationDomain(): string {
    return this.amplifyConfig.cognito.userPoolAuthUrl;
  }

  public getApiInvokeUrl(): string {
    return this.amplifyConfig.api.invokeUrl;
  }

  public async configure(): Promise<void> {
    const config = await this.getSourceAmplifyConfig();

    if (!config) {
      throw new Error('Unable to load config file.');
    }

    this.amplifyConfig = config;
    const { cognito } = config;
    const redirectUrl = this.getRedirectUrl();

    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: cognito.userPoolId,
          userPoolClientId: cognito.userPoolClientId,
          loginWith: {
            oauth: {
              domain: cognito.userPoolAuthUrl,
              scopes: ['openid', 'email', 'profile'],
              redirectSignIn: [redirectUrl],
              redirectSignOut: [redirectUrl],
              responseType: 'code',
              providers: [{ custom: 'azure' }],
            },
          },
        },
      },
    });
  }

  public async getCurrentUser(): Promise<{ email: string; name: string } | null> {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens?.idToken) return null;
      const payload = session.tokens.idToken.payload;
      return {
        email: (payload.email as string) ?? '',
        name: (payload.name as string) ?? '',
      };
    } catch {
      return null;
    }
  }

  public async getIdToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  }

  public async signIn(): Promise<void> {
    await signInWithRedirect({ provider: { custom: 'azure' } });
  }

  public async signOut(): Promise<void> {
    await signOut();
  }
}
